import {
  useLoginFastrMutation,
  useLogoutFastrMutation,
  FastrLoginResponse,
} from "@services/authApi";

import {
  localStorageService,
  sessionStorageService,
} from "@utils/storageService/StorageService";
import { useCallback } from "react";

interface LoginParams {
  pin: string;
}

type useAuthReturn = {
  login: [(params: LoginParams) => Promise<void>, boolean];
  logout: [() => Promise<void>, boolean];
  checkAndReauth: () => Promise<void>;
};

const REAUTH_BEFORE_TOKEN_EXPIRY_IN_MS = 60 * 2 * 1000; // 2 minutes

let loginRequestPromise: Promise<FastrLoginResponse> | null = null;
let logoutRequestPromise: Promise<void> | null = null;

/**
 * Method to get login and logout functions.
 *
 * NOTE: This hook is used in multiple places, so if you add any react
 * state here, it will be local to its the component its called in,
 * whereas login/logout is globally scoped.
 */
export default function useAuth(): useAuthReturn {
  const [loginFastr, { isLoading: isLoginLoading }] = useLoginFastrMutation();
  const [logoutFastr, { isLoading: isLogoutLoading }] =
    useLogoutFastrMutation();

  const login: (params: LoginParams) => Promise<void> = useCallback(
    async (params: LoginParams) => {
      const linkId = localStorageService.get("fastr_fastrId");
      const linkSecret = localStorageService.get("fastr_fastrHash");

      if (!linkId || !linkSecret) {
        throw new Error("Missing required authentication parameters");
      }

      loginRequestPromise = loginFastr({
        pin: params.pin,
        linkId,
        linkSecret,
      }).unwrap();

      let res;
      try {
        res = await loginRequestPromise;
      } catch (error) {
        console.log("Error in login:", error);
        throw error;
      } finally {
        loginRequestPromise = null;
      }

      sessionStorageService.set("fastr_token", res.access_token);
      sessionStorageService.set("fastr_token_createdAt", Date.now());
      sessionStorageService.set("fastr_token_expiresIn", res.expires_in);
      sessionStorageService.set("fastr_pin", params.pin);
    },
    [loginFastr]
  );

  const checkAndReauth: () => Promise<void> = useCallback(async () => {
    const pin = sessionStorageService.get("fastr_pin");
    const expiresIn = sessionStorageService.get("fastr_token_expiresIn");
    const createdAt = sessionStorageService.get("fastr_token_createdAt");

    if (
      !pin ||
      !expiresIn ||
      !createdAt ||
      loginRequestPromise ||
      logoutRequestPromise
    ) {
      return;
    }

    const expireTime = createdAt + expiresIn * 1000;
    const timeTillExpiry = expireTime - Date.now();

    if (timeTillExpiry > REAUTH_BEFORE_TOKEN_EXPIRY_IN_MS) {
      return;
    }

    try {
      await login({ pin });
    } catch (error) {
      console.error("Error in checkAndReauth:", error);
    }
  }, [login]);

  const logout = async () => {
    sessionStorageService.remove("fastr_pin");

    logoutRequestPromise = logoutFastr().unwrap();
    try {
      await logoutRequestPromise;
    } catch (error) {
      console.error("Error in logout:", error);
      throw error;
    } finally {
      logoutRequestPromise = null;
    }

    sessionStorageService.remove("fastr_token");
    sessionStorageService.remove("fastr_token_createdAt");
    sessionStorageService.remove("fastr_token_expiresIn");
  };

  return {
    login: [login, isLoginLoading],
    logout: [logout, isLogoutLoading],
    checkAndReauth,
  };
}
